import { useQuery } from '@tanstack/react-query';
import { useState } from 'react';
import { isCurrentFeedbackDraftScope, listFeedbackAuthoringDrafts } from '@/lib/api';
import { feedbackAuthoringDraftsKey, queryClient } from '@/lib/queryClient';
import type { FeedbackDraftScope } from '@/lib/types';

export function useFeedbackDrafts(scope: FeedbackDraftScope, enabled: boolean, search: string, cursor?: string) {
  const query = { search, cursor, limit: 20 };
  const requestKey = JSON.stringify([scope, search, cursor]);
  const [failure, setFailure] = useState<{ key: string; cause: unknown } | null>(null);
  const failed = failure?.key === requestKey;
  const result = useQuery({ queryKey: feedbackAuthoringDraftsKey(scope, query),
    queryFn: async ({ signal }) => {
      try { return await listFeedbackAuthoringDrafts(query, { scope, signal }); }
      catch (cause) {
        // Private transport retires its query cache on access/cursor errors. Keep the error
        // visible even when that retirement cancels this query's observer.
        if (isCurrentFeedbackDraftScope(scope) && !(cause instanceof DOMException && cause.name === 'AbortError'))
          setFailure({ key: requestKey, cause });
        throw cause;
      }
    },
    enabled: enabled && !failed, retry: false, refetchOnWindowFocus: false });
  return { ...result, loadError: failed ? failure.cause : result.error,
    restart: () => { void queryClient.invalidateQueries({ queryKey: feedbackAuthoringDraftsKey(scope, { search, limit: 20 }), exact: true }); },
    retry: () => { setFailure(null); void result.refetch(); } };
}
